import { useEffect, useMemo } from 'react';
import { useGlobalPanel } from '@frontend/panel-engine';
import { useSlidePanelShallowStore } from '@frontend/slide-panel';
import { Tray } from '@frontend/design-system';
import { useCallInsightPanelShallowStore } from '../hooks/use-call-insight-panel-store';
import { useScreens } from '../hooks/use-screens';
import { ScreenBaseProps, ScreenType } from '../types';
import { CallInsightPanelLoader } from './call-insight-panel-loader';
import { CallInsightScreen } from './screens/call-insight-screen';
import { CallQualityDrilldownScreen } from './screens/call-quality-drilldown-screen';
import { CallQualityScreen } from './screens/call-quality-screen';

const Screens: Record<ScreenType, React.ComponentType<ScreenBaseProps>> = {
  [ScreenType.CALL_INSIGHTS]: CallInsightScreen,
  [ScreenType.CALL_QUALITY]: CallQualityScreen,
  [ScreenType.CALL_QUALITY_DRILLDOWN]: CallQualityDrilldownScreen,
};

export const CallInsightDemoTray = () => {
  const { setShow } = useSlidePanelShallowStore('setShow');
  const { contexts, activeSource } = useCallInsightPanelShallowStore('contexts', 'activeSource');

  const ctx = activeSource && contexts[activeSource];

  const { screenType, data: payload, setScreen, reset } = useScreens(ScreenType.CALL_INSIGHTS);

  const Screen = useMemo(() => Screens[screenType] || Screens[ScreenType.CALL_INSIGHTS], [screenType]);

  const handleClose = () => {
    reset();
    setShow(false, 'callInsight');
  };

  useEffect(() => {
    reset();
  }, [ctx?.selectedCall?.id, reset]);

  if (!activeSource || !ctx?.selectedCall) {
    return null;
  }

  return (
    <Tray mountTarget='[data-settings-modal-content]' width='medium' css={{ padding: 0 }} show onClose={handleClose}>
      <Screen
        source={activeSource}
        key={ctx.selectedCall.id}
        setScreen={setScreen}
        data={payload}
        onClose={handleClose}
      />
    </Tray>
  );
};

export const CallInsightPanel = () => {
  const { isOpen, close: closeGlobal } = useGlobalPanel('call-insight');
  const { contexts, activeSource } = useCallInsightPanelShallowStore('contexts', 'activeSource');

  const ctx = activeSource && contexts[activeSource];

  const { screenType, data: payload, setScreen, reset } = useScreens(ScreenType.CALL_INSIGHTS);

  const Screen = useMemo(() => Screens[screenType] || Screens[ScreenType.CALL_INSIGHTS], [screenType]);

  const handleClose = () => {
    reset();
    closeGlobal();
  };

  useEffect(() => {
    reset();
  }, [ctx?.selectedCall?.id, reset]);

  if (!activeSource || !ctx?.selectedCall) {
    return <CallInsightPanelLoader withHeader onClose={closeGlobal} />;
  }

  if (!isOpen) return null;

  return (
    <Screen
      source={activeSource}
      key={ctx.selectedCall.id}
      setScreen={setScreen}
      data={payload}
      onClose={handleClose}
      panelStyles={{ overflowY: 'auto' }}
    />
  );
};
